import { useMemo, useState, useRef, useCallback } from 'react';
import { Box, FormControl, InputLabel, Select, MenuItem, Divider, Button } from '@mui/material';
import { Page, MRTTable } from '../../components';
import { useToastContext } from '../../hooks';
import { useQuery } from '@tanstack/react-query';
import { SoftDeleteAPI } from '../../fetch/soft-deletes';
import { getLocalDate } from '../../helpers/format';
import {
  createPersonColumns,
  createPropertyColumns,
  createCompanyColumns,
} from './columnDefinitions';
import { 
  SoftDeleteType, 
  IPersonSoftDelete, 
  IPropertySoftDelete, 
  ICompanySoftDelete 
} from '../../models/soft-deletes';

type SoftDeleteEntity = IPersonSoftDelete | IPropertySoftDelete | ICompanySoftDelete;

export const SoftDeletes = () => {
  const { showToast } = useToastContext();
  const [selectedType, setSelectedType] = useState<string | number>(SoftDeleteType.Person);
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [sorting, setSorting] = useState<any[]>([]);
  const [globalFilter, setGlobalFilter] = useState('');
  const searchDebounceRef = useRef<NodeJS.Timeout | null>(null);

  const {
    data: peopleData,
    isFetching: isFetchingPeople,
    refetch: refetchPeople,
  } = useQuery({
    queryKey: ['softDeletes', 'people', page, perPage, sorting, globalFilter],
    queryFn: () => SoftDeleteAPI.getPeople({
      page: page + 1,
      perPage,
      ...(sorting.length > 0 && { sortBy: sorting[0].id, sortDirection: sorting[0].desc ? 'Desc' : 'Asc' }),
      ...(globalFilter && { search: globalFilter }),
    }),
    enabled: selectedType === SoftDeleteType.Person,
    keepPreviousData: true,
    onError: (error: any) => {
      showToast('error', error?.detail || 'Error loading people data. Please try again.');
    },
  });

  const {
    data: propertiesData,
    isFetching: isFetchingProperties,
    refetch: refetchProperties,
  } = useQuery({
    queryKey: ['softDeletes', 'properties', page, perPage, sorting, globalFilter],
    queryFn: () => SoftDeleteAPI.getProperties({
      page: page + 1,
      perPage,
      ...(sorting.length > 0 && { sortBy: sorting[0].id, sortDirection: sorting[0].desc ? 'Desc' : 'Asc' }),
      ...(globalFilter && { search: globalFilter }),
    }),
    enabled: selectedType === SoftDeleteType.Property,
    keepPreviousData: true,
    onError: (error: any) => {
      showToast('error', error?.detail || 'Error loading properties data. Please try again.');
    },
  });

  const {
    data: companiesData,
    isFetching: isFetchingCompanies,
    refetch: refetchCompanies,
  } = useQuery({
    queryKey: ['softDeletes', 'companies', page, perPage, sorting, globalFilter],
    queryFn: () => SoftDeleteAPI.getCompanies({
      page: page + 1,
      perPage,
      ...(sorting.length > 0 && { sortBy: sorting[0].id, sortDirection: sorting[0].desc ? 'Desc' : 'Asc' }),
      ...(globalFilter && { search: globalFilter }),
    }),
    enabled: selectedType === SoftDeleteType.Company,
    keepPreviousData: true,
    onError: (error: any) => {
      showToast('error', error?.detail || 'Error loading companies data. Please try again.');
    },
  });

  const handleRestore = useCallback(async (id: number, type: string) => {
    try {
      switch (type) {
        case SoftDeleteType.Person:
          await SoftDeleteAPI.restorePerson(id);
          break;
        case SoftDeleteType.Property:
          await SoftDeleteAPI.restoreProperty(id);
          break;
        case SoftDeleteType.Company:
          await SoftDeleteAPI.restoreCompany(id);
          break;
        default: {
          showToast('error', 'Unknown type');
          return;
        }
      }
      showToast(
        'success',
        `${type.charAt(0).toUpperCase() + type.slice(1)} restored successfully.`
      );
      // Reset to first page and refresh data
      setPage(0);
      // Force refetch for the current type
      if (type === SoftDeleteType.Person) {
        refetchPeople();
      } else if (type === SoftDeleteType.Property) {
        refetchProperties();
      } else if (type === SoftDeleteType.Company) {
        refetchCompanies();
      }
    } catch (error: any) {
      const err = error as { detail?: string };
      showToast('error', err?.detail || `Error restoring ${type}. Please try again.`);
    }
  }, [refetchCompanies, refetchPeople, refetchProperties, showToast]);

  const formatDate = useCallback((dateValue: string | null): string => {
    if (!dateValue) return '';
    try {
      return getLocalDate(dateValue);
    } catch {
      return '';
    }
  }, []);

  const personColumns = useMemo(
    () => createPersonColumns(handleRestore, formatDate),
    [handleRestore, formatDate]
  );

  const propertyColumns = useMemo(
    () => createPropertyColumns(handleRestore, formatDate),
    [handleRestore, formatDate]
  );

  const companyColumns = useMemo(
    () => createCompanyColumns(handleRestore, formatDate),
    [handleRestore, formatDate]
  );

  const getCurrentData = (): SoftDeleteEntity[] => {
    switch (selectedType) {
      case SoftDeleteType.Person:
        return peopleData?.records || [];
      case SoftDeleteType.Property:
        return propertiesData?.records || [];
      case SoftDeleteType.Company:
        return companiesData?.records || [];
      default:
        return [];
    }
  };

  const getCurrentTotalCount = () => {
    switch (selectedType) {
      case SoftDeleteType.Person:
        return peopleData?.totalRecordCount || 0;
      case SoftDeleteType.Property:
        return propertiesData?.totalRecordCount || 0;
      case SoftDeleteType.Company:
        return companiesData?.totalRecordCount || 0;
      default:
        return 0;
    }
  };

  const getCurrentColumns = () => {
    switch (selectedType) {
      case SoftDeleteType.Person:
        return personColumns;
      case SoftDeleteType.Property:
        return propertyColumns;
      case SoftDeleteType.Company:
        return companyColumns;
      default:
        return personColumns;
    }
  };

  const getCurrentLoading = (): boolean => {
    switch (selectedType) {
      case SoftDeleteType.Person:
        return isFetchingPeople;
      case SoftDeleteType.Property:
        return isFetchingProperties;
      case SoftDeleteType.Company:
        return isFetchingCompanies;
      default:
        return isFetchingPeople;
    }
  };

  return (
    <Page title="Manage Soft Deletes">
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Entity Type</InputLabel>
          <Select
            value={selectedType}
            label="Entity Type"
            onChange={(e) => {
              setSelectedType(e.target.value);
              setPage(0);
              setPerPage(10);
              setSorting([]);
              setGlobalFilter('');
            }}
          >
            <MenuItem value={SoftDeleteType.Person}>Person</MenuItem>
            <MenuItem value={SoftDeleteType.Property}>Property</MenuItem>
            <MenuItem value={SoftDeleteType.Company}>Company</MenuItem>
          </Select>
        </FormControl>
        <Divider orientation="vertical" flexItem />
        <Button
          type="button"
          variant="outlined"
          size="small"
          onClick={() => {
            setSelectedType(SoftDeleteType.Person);
            setPage(0);
            setPerPage(10);
            setSorting([]);
            setGlobalFilter('');
          }}
        >
          Clear Filters
        </Button>
      </Box>
      
      <MRTTable
        columns={getCurrentColumns() as any}
        data={getCurrentData()}
        loading={getCurrentLoading()}
        manualPagination
        manualSorting
        enableFilterMatchHighlighting={false}
        tableContainerSx={{
          '&::-webkit-scrollbar': { height: 5 },
          '&::-webkit-scrollbar-track': { background: 'transparent' },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#b4b4b4',
            borderRadius: 4,
          },
        }}
        totalRowCount={getCurrentTotalCount()}
        pageIndex={page}
        pageSize={perPage}
        onPaginationChange={(updater: any) => {
          const next = typeof updater === 'function' ? updater({ pageIndex: page, pageSize: perPage }) : updater;
          if (typeof next?.pageIndex === 'number') setPage(next.pageIndex);
          if (typeof next?.pageSize === 'number') setPerPage(next.pageSize);
        }}
        onSortingChange={(updater: any) => {
          const next = typeof updater === 'function' ? updater(sorting) : updater;
          setSorting(next || []);
          // Reset to first page when sorting changes
          setPage(0);
        }}
        sorting={sorting}
        globalFilter={globalFilter}
        onGlobalFilterChange={(val: string) => {
          const v = typeof val === 'string' ? val : '';
          setGlobalFilter(v);
          // Debounce page reset to avoid excessive refetches
          if (searchDebounceRef.current) {
            clearTimeout(searchDebounceRef.current);
          }
          searchDebounceRef.current = setTimeout(() => {
            setPage(0);
          }, 300);
        }}
      />
    </Page>
  );
};
